/**
 * 文件夹相关 API Hooks
 */
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { folderApi } from '@/services/api';
import { useOrgContextStore } from '@/stores/orgContextStore';
import type {
  Folder,
  CreateFolderDto,
  UpdateFolderDto,
  FolderCategory,
  FolderDeleteProjectHandling,
} from '@/types/folder';

// Query Keys
export const folderKeys = {
  all: ['folders'] as const,
  lists: () => [...folderKeys.all, 'list'] as const,
  list: (
    activeOrgId?: string | null,
    parentId?: string | null,
    folderCategory?: FolderCategory,
    page?: number,
    pageSize?: number
  ) => [...folderKeys.lists(), { activeOrgId, parentId, folderCategory, page, pageSize }] as const,
  trees: () => [...folderKeys.all, 'tree'] as const,
  tree: (
    activeOrgId?: string | null,
    maxDepth?: number,
    includeProjects?: boolean,
    includeSubprojects?: boolean,
    includeFullProjectFields?: boolean,
    scope?: 'mine' | 'accessible'
  ) =>
    [
      ...folderKeys.trees(),
      {
        activeOrgId,
        maxDepth,
        includeProjects,
        includeSubprojects,
        includeFullProjectFields,
        scope,
      },
    ] as const,
  details: () => [...folderKeys.all, 'detail'] as const,
  detail: (activeOrgId: string | null, id: string) =>
    [...folderKeys.details(), activeOrgId, id] as const,
  paths: () => [...folderKeys.all, 'path'] as const,
  path: (activeOrgId: string | null, id: string) =>
    [...folderKeys.paths(), activeOrgId, id] as const,
  stats: () => [...folderKeys.all, 'stats'] as const,
  stat: (activeOrgId: string | null, id: string, recursive?: boolean) =>
    [...folderKeys.stats(), activeOrgId, id, { recursive }] as const,
};

/**
 * 获取文件夹列表
 */
export function useFolders(params?: {
  parentId?: string | null;
  folderCategory?: FolderCategory;
  page?: number;
  pageSize?: number;
}) {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useQuery({
    queryKey: folderKeys.list(
      activeOrgId,
      params?.parentId,
      params?.folderCategory,
      params?.page,
      params?.pageSize
    ),
    queryFn: () => folderApi.getAll(params),
    staleTime: 1000 * 60 * 5, // 5分钟
    gcTime: 1000 * 60 * 30, // 30分钟
  });
}

/**
 * 获取文件夹树形结构
 */
export function useFolderTree(
  params?: {
    maxDepth?: number;
    includeProjects?: boolean;
    includeSubprojects?: boolean;
    includeFullProjectFields?: boolean;
    scope?: 'mine' | 'accessible';
  },
  enabled: boolean = true,
  /** 路由上下文覆盖：string=指定组织，null=明确个人，undefined=沿用 store/sessionStorage */
  urlOrgId?: string | null | undefined
) {
  const storeOrgId = useOrgContextStore((s) => s.activeOrgId);
  const hasUrlOrgOverride = urlOrgId !== undefined;
  const normalizedUrlOrgId = urlOrgId !== undefined && urlOrgId !== '' ? urlOrgId : null;
  const requestOrgId = hasUrlOrgOverride ? normalizedUrlOrgId : storeOrgId;

  return useQuery({
    queryKey: folderKeys.tree(
      requestOrgId,
      params?.maxDepth,
      params?.includeProjects,
      params?.includeSubprojects,
      params?.includeFullProjectFields,
      params?.scope
    ),
    queryFn: () =>
      folderApi.getTree(
        params,
        hasUrlOrgOverride ? { activeOrgId: normalizedUrlOrgId } : undefined
      ),
    enabled,
    staleTime: 1000 * 60 * 5, // 5分钟
    gcTime: 1000 * 60 * 30, // 30分钟
  });
}

/**
 * 获取单个文件夹
 */
export function useFolder(
  id: string | null,
  /** 路由上下文覆盖：string=指定组织，null=明确个人，undefined=沿用 store/sessionStorage */
  urlOrgId?: string | null | undefined
) {
  const queryClient = useQueryClient();
  const storeOrgId = useOrgContextStore((s) => s.activeOrgId);
  const hasUrlOrgOverride = urlOrgId !== undefined;
  const normalizedUrlOrgId = urlOrgId !== undefined && urlOrgId !== '' ? urlOrgId : null;
  const requestOrgId = hasUrlOrgOverride ? normalizedUrlOrgId : storeOrgId;

  return useQuery({
    queryKey: folderKeys.detail(requestOrgId, id!),
    queryFn: () =>
      folderApi.getById(
        id!,
        true,
        hasUrlOrgOverride ? { activeOrgId: normalizedUrlOrgId } : undefined
      ),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5分钟
    gcTime: 1000 * 60 * 30, // 30分钟
    initialData: () => {
      // 尝试从列表缓存中查找
      const lists = queryClient.getQueriesData<{ items: Folder[] }>({
        queryKey: folderKeys.lists(),
      });
      for (const [, data] of lists) {
        if (data?.items) {
          const folder = data.items.find((f) => f.id === id);
          if (folder) return folder;
        }
      }
      return undefined;
    },
  });
}

/**
 * 获取文件夹路径（面包屑）
 */
export function useFolderPath(
  id: string | null,
  /** 路由上下文覆盖：string=指定组织，null=明确个人，undefined=沿用 store/sessionStorage */
  urlOrgId?: string | null | undefined
) {
  const storeOrgId = useOrgContextStore((s) => s.activeOrgId);
  const hasUrlOrgOverride = urlOrgId !== undefined;
  const normalizedUrlOrgId = urlOrgId !== undefined && urlOrgId !== '' ? urlOrgId : null;
  const requestOrgId = hasUrlOrgOverride ? normalizedUrlOrgId : storeOrgId;

  return useQuery({
    queryKey: folderKeys.path(requestOrgId, id!),
    queryFn: () =>
      folderApi.getPath(id!, hasUrlOrgOverride ? { activeOrgId: normalizedUrlOrgId } : undefined),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * 创建文件夹
 */
export function useCreateFolder() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: (data: CreateFolderDto) => folderApi.create(data),
    onSuccess: (newFolder) => {
      // 失效相关查询
      queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: folderKeys.trees() });

      // 如果有父文件夹，失效父文件夹详情
      if (newFolder.parentFolderId) {
        queryClient.invalidateQueries({
          queryKey: folderKeys.detail(activeOrgId, newFolder.parentFolderId),
        });
      }
    },
  });
}

/**
 * 更新文件夹
 */
export function useUpdateFolder() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFolderDto }) => folderApi.update(id, data),
    onSuccess: (updatedFolder, { id }) => {
      // 更新详情缓存
      queryClient.setQueryData<Folder>(folderKeys.detail(activeOrgId, id), updatedFolder);

      // 失效列表和树
      queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: folderKeys.trees() });
    },
  });
}

/**
 * 删除文件夹
 */
export function useDeleteFolder() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({
      id,
      cascade = false,
      projectHandling,
    }: {
      id: string;
      cascade?: boolean;
      projectHandling?: FolderDeleteProjectHandling;
    }) => folderApi.delete(id, cascade, projectHandling),
    onSuccess: (_, { id }) => {
      // 移除详情缓存
      queryClient.removeQueries({ queryKey: folderKeys.detail(activeOrgId, id) });

      // 失效列表和树
      queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: folderKeys.trees() });
    },
  });
}

/**
 * 移动文件夹
 */
export function useMoveFolder() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useMutation({
    mutationFn: ({ id, targetParentId }: { id: string; targetParentId: string | null }) =>
      folderApi.move(id, targetParentId),
    onSuccess: (movedFolder, { id }) => {
      // 更新详情缓存
      queryClient.setQueryData<Folder>(folderKeys.detail(activeOrgId, id), movedFolder);

      // 失效列表和树
      queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: folderKeys.trees() });

      // 失效路径缓存
      queryClient.invalidateQueries({ queryKey: folderKeys.paths() });
    },
  });
}

/**
 * 克隆文件夹
 */
export function useCloneFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      mode,
      targetName,
      targetParentId,
    }: {
      id: string;
      mode: 'content' | 'recursive';
      targetName?: string;
      targetParentId?: string;
    }) => folderApi.clone(id, { mode, targetName, targetParentId }),
    onSuccess: () => {
      // 失效列表和树
      queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: folderKeys.trees() });
    },
  });
}

/**
 * 获取文件夹统计信息
 */
export function useFolderStats(id: string | null, recursive = false) {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useQuery({
    queryKey: folderKeys.stat(activeOrgId, id!, recursive),
    queryFn: () => folderApi.getStats(id!, recursive),
    enabled: !!id,
    staleTime: 1000 * 60, // 1分钟
  });
}

// ========================================================================
// 缓存操作工具函数（供其他 hooks 调用，避免直接 import folderKeys）
// ========================================================================

/**
 * 失活文件夹树缓存。
 * 在创建/删除/移动项目或文件夹，或修改项目名称后调用。
 */
export function invalidateFolderTrees(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: folderKeys.trees() });
}

/**
 * 失活文件夹列表缓存。
 * 在创建/删除/移动文件夹，或修改文件夹分享状态后调用。
 */
export function invalidateFolderLists(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
}

/**
 * 更新文件夹树缓存数据（用于乐观更新，如重命名剧本时同步更新文件夹树中的项目名）。
 */
export function setFolderTreesData(
  queryClient: QueryClient,
  updater: (old: unknown) => unknown
): void {
  queryClient.setQueriesData({ queryKey: folderKeys.trees() }, updater);
}
